<template lang="html">
  <nav class="bg-primary fixed w-full z-20">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <div class="flex items-center">
          <router-link :to="{ name: 'Home' }">
            <img
              class="h-7 mr-0"
              src="@/assets/screenlab-logo.svg"
              alt="Workflow"
            />
          </router-link>
          <div class="hidden md:block">
            <div class="ml-10 flex items-baseline space-x-4">
              <!-- Current: "bg-gray-900 text-white", Default: "text-gray-300 hover:bg-gray-700 hover:text-white" -->
              <router-link
                :to="{ name: 'Home', params: {} }"
                class="text-white hover:bg-blue-800 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >Start a scan</router-link
              >

              <router-link
                :to="{ name: 'Scans', params: {} }"
                class="text-white hover:bg-blue-800 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >View your scans</router-link
              >
            </div>
          </div>
        </div>
        <div class="justify-end flex flex-row">
          <div class="ml-4 flex items-center md:ml-6">
            <div
              class="text-white flex flex-row items-center mr-2"
              title="scans in progress"
              v-if="scansInProgress"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 animate-reverse-spin"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                  clip-rule="evenodd"
                />
              </svg>
              {{ scansInProgress }}
            </div>

            <div class="relative">
              <div
                class="block absolute items-center px-2 py-0.5 -mt-1 -ml-1 rounded-full text-xs font-medium bg-red-600 text-white shadow-md"
                v-if="notifications.length > 0"
              >
                {{ notifications.length }}
              </div>
              <button
                :class="notificationClass"
                class="bg-primary p-1 rounded-full hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
              >
                <span class="sr-only">View notifications</span>
                <!-- Heroicon name: outline/bell -->

                <svg
                  class="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              </button>

              <transition name="slide-fade">
                <div
                  class="absolute origin-top-right right-0"
                  v-if="notifications.length > 0"
                >
                  <transition-group name="slide-fade" tag="ul">
                    <li
                      class="bg-white rounded shadow-lg w-auto text-xs p-2 mb-2"
                      v-for="notification in notifications"
                      :key="notification"
                      @click="$emit('remove-notification', notification)"
                      v-html="notification"
                    ></li>
                  </transition-group>
                </div>
              </transition>
            </div>

            <!-- Profile dropdown -->
            <div class="ml-3 relative hidden md:block">
              <div>
                <button
                  type="button"
                  @click="showMenu = !showMenu"
                  class="max-w-xs bg-primary rounded-full flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                  id="user-menu"
                  aria-expanded="false"
                  aria-haspopup="true"
                >
                  <span class="sr-only">Open user menu</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-white h-7 w-7 fill-current"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="text-white font-bold ml-2">{{
                    user.attributes.name
                  }}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-white h-4 w-4 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    v-if="!showMenu"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-white h-4 w-4 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    v-else
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
              </div>

              <!--
                Dropdown menu, show/hide based on menu state.

                Entering: "transition ease-out duration-100"
                  From: "transform opacity-0 scale-95"
                  To: "transform opacity-100 scale-100"
                Leaving: "transition ease-in duration-75"
                  From: "transform opacity-100 scale-100"
                  To: "transform opacity-0 scale-95"
              -->
              <div
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu"
                v-show="showMenu"
              >
                <router-link
                  :to="{ name: 'Account', params: {} }"
                  @click.native="showMenu = false"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                  >Your Account</router-link
                >
                <router-link
                  :to="{ name: 'Settings', params: {} }"
                  @click.native="showMenu = false"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                  >Settings</router-link
                >
                <a
                  href="#"
                  @click="signOut()"
                  class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  role="menuitem"
                  >Sign out</a
                >
              </div>
            </div>
          </div>

          <!-- insert an orange button here -->
          <div>
            <router-link
              v-if="!user.attributes['custom:stripe_sub_id']"
              :to="{ name: 'Account', params: {} }"
              @click.native="showMenu = false"
              class="block btn ml-4"
              role="menuitem"
              >Upgrade now</router-link
            >
          </div>

          <div class="-mr-2 flex md:hidden">
            <!-- Mobile menu button -->
            <button
              type="button"
              class="bg-primary inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
              aria-controls="mobile-menu"
              aria-expanded="false"
              @click="showMobileMenu = !showMobileMenu"
            >
              <span class="sr-only">Open main menu</span>
              <!--
              Heroicon name: outline/menu

              Menu open: "hidden", Menu closed: "block"
            -->
              <svg
                class="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <!--
              Heroicon name: outline/x

              Menu open: "block", Menu closed: "hidden"
            -->
              <svg
                class="hidden h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile menu, show/hide based on menu state. -->
    <div class="md:hidden" id="mobile-menu" v-if="showMobileMenu">
      <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
        <!-- Current: "bg-gray-900 text-white", Default: "text-gray-300 hover:bg-gray-700 hover:text-white" -->
        <router-link
          :to="{ name: 'Home', params: {} }"
          class="block px-4 py-2 text-sm text-white hover:bg-blue-800"
          @click.native="showMobileMenu = false"
          >Start a scan</router-link
        >

        <router-link
          :to="{ name: 'Scans', params: {} }"
          class="block px-4 py-2 text-sm text-white hover:bg-blue-800"
          @click.native="showMobileMenu = false"
          >View your scans</router-link
        >
      </div>
      <div class="pt-4 pb-3 border-t border-gray-700">
        <div class="mt-3 px-2 space-y-1">
          <router-link
            :to="{ name: 'Account', params: {} }"
            @click.native="showMobileMenu = false"
            class="block px-4 py-2 text-sm text-white hover:bg-blue-800"
            role="menuitem"
            >Your Account</router-link
          >
          <router-link
            :to="{ name: 'Settings', params: {} }"
            @click.native="showMobileMenu = false"
            class="block px-4 py-2 text-sm text-white hover:bg-blue-800"
            role="menuitem"
            >Settings</router-link
          >
          <a
            href="#"
            @click="signOut()"
            class="block px-4 py-2 text-sm text-white hover:bg-blue-800"
            role="menuitem"
            >Sign out</a
          >
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { Auth } from "aws-amplify";

export default {
  name: "Navbar",
  components: {},
  props: ["user", "notifications", "scansInProgress"],
  data() {
    return {
      showMenu: false,
      showMobileMenu: false,
    };
  },
  methods: {
    async signOut() {
      this.showMenu = false;
      try {
        await Auth.signOut();
        this.$emit("signed-out");
      } catch (error) {
        console.log("error signing out: ", error);
      }
    },
  },
  computed: {
    notificationClass() {
      if (this.notifications.length < 1) return "text-gray-400";
      return "text-white";
    },
  },
};
</script>

<style lang="css">
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
</style>
